import { FC, useMemo, useRef, useState } from "react";
import { Alert, Box, Paper } from "@mui/material";
import {
  DataGridPro as DataGrid,
  GridColDef,
  GridLoadingOverlay,
  GridPaginationModel,
  GridSortModel,
  useGridApiRef,
  GridRowParams,
  GridFilterModel,
  getGridStringOperators,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import moment from "moment";

import Pagination from "src/Components/DataGrid/Pagination";
import { DeductionConsentEmployee, DeductionConsentFile } from "./models";
import { fetchDeductionConsentDocuments } from "./api";
import { useFetchDeductionConsents } from "./query";
import {
  formatDateTime,
  transformSortParams,
  transformFilterParams,
  calculateDetailPanelHeight,
  getAlternatingRowClassName,
} from "./utils";
import DeductionConsentsExpandedRow from "./DeductionConsentsExpandedRow";
import ExpandableIdCell from "./ExpandableIdCell";

import { dynamicNotification } from "src/utils/Notifications";
import __ from "src/utils/Translations";

const DeductionConsentsTable: FC = () => {
  const apiRef = useGridApiRef();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
  const [documentsByEmployeeId, setDocumentsByEmployeeId] = useState<
    Record<string, DeductionConsentFile[]>
  >({});
  const [loadingDocumentsFor, setLoadingDocumentsFor] = useState<Set<string>>(
    new Set()
  );

  const stringFilterOperators = useMemo(
    () =>
      getGridStringOperators().filter((operator) =>
        ["contains"].includes(operator.value)
      ),
    []
  );

  const sortParams = useMemo(() => transformSortParams(sortModel), [sortModel]);

  const filterParams = useMemo(
    () => transformFilterParams(filterModel),
    [filterModel]
  );

  const { data, isFetching, isError, error } = useFetchDeductionConsents({
    page: paginationModel.page,
    size: paginationModel.pageSize,
    sort: sortParams,
    ...filterParams,
  });

  const rowCountRef = useRef(data?.page?.totalElements || 0);
  const rowCount = useMemo(() => {
    if (data?.page?.totalElements !== undefined) {
      rowCountRef.current = data.page.totalElements;
    }
    return rowCountRef.current;
  }, [data?.page?.totalElements]);

  const columns = useMemo(
    () =>
      [
        {
          headerName: __("ID"),
          field: "id",
          width: 400,
          sortable: false,
          filterable: false,
          renderCell: (params: GridRenderCellParams<DeductionConsentEmployee>) => {
            const rowIdStr = params.id as string;
            return (
              <ExpandableIdCell 
                id={params.row.id}
                isExpanded={expandedRowIds.has(rowIdStr)}
                onToggle={async () => {
                  const isExpanding = !expandedRowIds.has(rowIdStr);
                  apiRef.current?.toggleDetailPanel(params.id);
                  setExpandedRowIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(rowIdStr)) {
                      next.delete(rowIdStr);
                    } else {
                      next.add(rowIdStr);
                    }
                    return next;
                  });

                  if (
                    !isExpanding ||
                    documentsByEmployeeId[rowIdStr] ||
                    loadingDocumentsFor.has(rowIdStr)
                  ) {
                    return;
                  }

                  setLoadingDocumentsFor((prev) => new Set(prev).add(rowIdStr));
                  try {
                    const files = await fetchDeductionConsentDocuments(rowIdStr, {
                      signal: new AbortController().signal,
                    });
                    setDocumentsByEmployeeId((prev) => ({
                      ...prev,
                      [rowIdStr]: files,
                    }));
                  } catch (error) {
                    dynamicNotification(
                      __("Nie udało się pobrać dokumentów pracownika."),
                      "error"
                    );
                  } finally {
                    setLoadingDocumentsFor((prev) => {
                      const next = new Set(prev);
                      next.delete(rowIdStr);
                      return next;
                    });
                  }
                }}
              />
            );
          },
        },
        {
          headerName: __("Imię"),
          field: "firstName",
          type: "string",
          width: 200,
          filterOperators: stringFilterOperators,
        },
        {
          headerName: __("Nazwisko"),
          field: "lastName",
          type: "string",
          width: 200,
          filterOperators: stringFilterOperators,
        },
        {
          headerName: __("Numer pracownika"),
          field: "fk",
          type: "string",
          width: 200,
          filterOperators: stringFilterOperators,
          sortable: false,
        },
        {
          headerName: __("Ostatnia modyfikacja pracownika"),
          field: "lastUpdateDate",
          type: "date",
          valueGetter: (value) => value && new Date(value),
          renderCell: ({ value }) => (
            <span title={value && formatDateTime(value)}>
              {value && moment(value).format("YYYY-MM-DD HH:mm:ss")}
            </span>
          ),
          width: 300,
          filterable: false,
        },
      ] as GridColDef<DeductionConsentEmployee>[],
    [apiRef, documentsByEmployeeId, expandedRowIds, loadingDocumentsFor, stringFilterOperators]
  );

  const getDetailPanelContent = (params: GridRowParams<DeductionConsentEmployee>) => {
    const rowId = params.id as string;
    return (
      <DeductionConsentsExpandedRow
        files={documentsByEmployeeId[rowId] || []}
        isLoading={loadingDocumentsFor.has(rowId)}
        firstName={params.row.firstName}
        lastName={params.row.lastName}
      />
    );
  };

  const getDetailPanelHeight = (params: GridRowParams<DeductionConsentEmployee>) => {
    const rowId = params.id as string;
    const filesCount = documentsByEmployeeId[rowId]?.length || 0;
    return calculateDetailPanelHeight(filesCount);
  };

  const getRowClassName = (params: GridRowParams<DeductionConsentEmployee>) =>
    getAlternatingRowClassName(data?.page?.content || [], params.id as string);

  return (
    <Paper
      sx={{
        padding: "1rem",
      }}
    >
      {isError && (
        <Alert variant="outlined" color="warning" sx={{ mb: 2 }}>
          {String(error)}
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 400,
        }}
      >
        <DataGrid
          apiRef={apiRef}
          columns={columns}
          getRowId={(row) => row.id}
          rows={data?.page?.content || []}
          rowCount={rowCount}
          loading={isFetching}
          disableDensitySelector
          disableRowSelectionOnClick
          disableVirtualization
          showCellVerticalBorder={false}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          getRowClassName={getRowClassName}
          slots={{
            loadingOverlay: () => (
              <GridLoadingOverlay
                variant="linear-progress"
                noRowsVariant="skeleton"
              />
            ),
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: false,
            },
            basePagination: {
              material: {
                ActionsComponent: Pagination,
              },
            },
          }}
          sx={{
            "& .MuiDataGrid-cell[data-field='__detail_panel_toggle__']": {
              display: "none",
            },
            "& .MuiDataGrid-columnHeader[data-field='__detail_panel_toggle__']": {
              display: "none",
            },
            "& .MuiDataGrid-row.row-odd": {
              backgroundColor: "grey.100",
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default DeductionConsentsTable;

